'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contextualNodeAttributes"];
import { LinkIcon } from 'foundations-assets';
import CanvasToolbarButton from 'rich-text-lib/components/toolbar/ToolbarButton';
import tinymce from 'tinymce';
import LinkPopoverManager from '../components/LinkPopoverManager';
import InsertButton from '../components/toolbar/InsertButton';
import { LINK_SHORTCUT, hslinkConfig } from '../constants/hslink';
import { INLINE_TARGET_ATTR } from '../constants/inlineTarget';
import { CANVAS_LINK } from '../constants/toolbar';
import { i18nWithNameSpace } from '../utils/i18n';
import { iconIsOnlyChild, nodeIsIconElement } from '../utils/icon';
import { imageIsOnlyChild } from '../utils/image';
import { isNodeWithinLink, isNodeWithinTempLink, nodeIsLinkNode, removeUnInsertedLinks } from '../utils/link';
import { tokenIsOnlyChild } from '../utils/token';
import { getPreventDefaultOnLinkClick } from './shared';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nKey = i18nWithNameSpace('TinymcePlugins.hslink');
const addCommands = editor => {
  // Override the builtin insert link command so that spaces are not automatically replaced
  // with %20 since they often are often used in HubL
  editor.addCommand('mceInsertLink', (ui, value) => {
    const {
      dom,
      formatter,
      selection
    } = editor;
    if (typeof value === 'string') {
      value = {
        href: value
      };
    }
    const anchor = dom.getParent(selection.getNode(), 'a');

    // Remove existing links if there could be child links or that the href isn't specified
    if (!anchor || !value.href) {
      formatter.remove('link');
    }

    // Apply new link to selection
    if (value.href) {
      formatter.apply('link', value, anchor);
    }
  });
};
export const addControls = ({
  editor,
  pluginName,
  onSelectFile,
  onChangeLink,
  fetchPages,
  fetchPageBySlug,
  fetchMeetingsByUsers,
  fetchMeetingBySlug,
  fetchAnchors,
  fetchFileByUrl,
  [hslinkConfig.ALLOW_FILE_DOWNLOAD]: allowFileDownload = true,
  [hslinkConfig.ALLOW_MEETING_LINKS]: allowMeetingLink,
  [hslinkConfig.ALLOW_ANCHOR_LINKS]: allowAnchorLinks,
  [hslinkConfig.ALLOW_RAW_LINKS_ONLY]: allowRawLinksOnly = false,
  [hslinkConfig.EMAIL_SUBSCRIPTION_LINK_TYPES]: emailSubscriptionLinkTypes = [],
  [hslinkConfig.HIDE_TARGET_BLANK_AND_NO_FOLLOW]: hideTargetBlankNoFollow,
  [hslinkConfig.ALLOW_EMAIL_WEB_VERSION_LINK]: allowEmailWebVersionLink,
  [hslinkConfig.OMIT_DOMAIN_REWRITE_ATTR]: omitDomainRewriteAttr
}) => {
  const handleClick = ({
    isActive
  }) => {
    const selectedNode = editor.selection.getNode();
    if (nodeIsIconElement(editor, selectedNode)) {
      editor.fire('InsertLinkOnIcon');
      return;
    }
    if (imageIsOnlyChild(editor, selectedNode)) {
      // Open link tab in image popover
      const element = selectedNode.nodeName === 'IMG' ? selectedNode : selectedNode.querySelector('img');
      editor.fire('InsertLinkOnImage', {
        element
      });
      return;
    }
    if (isActive) {
      editor.fire('ToggleLinkEditPopover');
    } else {
      editor.fire('OpenLinkPopover');
    }
  };
  const isActiveHandler = ({
    element
  }) => {
    if (!element) return false;
    return isNodeWithinLink(editor, element);
  };
  const isActiveNodeHandler = ({
    element
  }) => {
    if (!element) return null;
    const isWithinLink = isNodeWithinLink(editor, element) || isNodeWithinTempLink(editor, element);
    if (element.hasAttribute(INLINE_TARGET_ATTR)) {
      return element;
    }
    if (!isWithinLink || imageIsOnlyChild(editor, element) || tokenIsOnlyChild(editor, element) || iconIsOnlyChild(editor, element)) {
      return null;
    }
    if (nodeIsLinkNode(element)) return element;
    return editor.dom.getParent(element, 'a', editor.getBody());
  };
  tinymce.canvas.addNodeChangeHandler(editor, {
    eventKey: pluginName,
    isActiveNodeHandler
  });
  tinymce.canvas.addButton(editor, CANVAS_LINK, {
    renderControl: props => {
      const rest = _objectWithoutPropertiesLoose(props, _excluded);
      return /*#__PURE__*/_jsx(InsertButton, Object.assign({
        ButtonComponent: CanvasToolbarButton,
        "data-test-id": "insert-link-button",
        editor: editor,
        iconName: "link",
        icon: LinkIcon,
        i18nKey: i18nKey,
        onClick: handleClick,
        onClickShortcut: LINK_SHORTCUT,
        isActiveHandler: isActiveHandler
      }, rest));
    }
  });
  tinymce.canvas.addMenuItem(editor, CANVAS_LINK, {
    text: i18nKey('menuItem'),
    onClick: handleClick
  });
  tinymce.canvas.addPopover(editor, pluginName, {
    renderPopover(renderProps) {
      return /*#__PURE__*/_jsx(LinkPopoverManager, Object.assign({
        editor: editor,
        removeUnInsertedLinks: removeUnInsertedLinks(editor),
        onSelectFile: onSelectFile,
        onChangeLink: onChangeLink,
        fetchPages: fetchPages,
        showRefAndTargetControls: !hideTargetBlankNoFollow,
        useEmailSubscription: emailSubscriptionLinkTypes.length > 0,
        allowFileDownload: allowFileDownload,
        allowMeetingLink: allowMeetingLink,
        allowAnchorLinks: allowAnchorLinks,
        allowRawLinksOnly: allowRawLinksOnly,
        emailSubscriptionLinkTypes: emailSubscriptionLinkTypes,
        fetchPageBySlug: fetchPageBySlug,
        fetchFileByUrl: fetchFileByUrl,
        fetchMeetingsByUsers: fetchMeetingsByUsers,
        fetchAnchors: fetchAnchors,
        fetchMeetingBySlug: fetchMeetingBySlug,
        eventKey: pluginName,
        allowEmailWebVersionLink: allowEmailWebVersionLink,
        omitDomainRewriteAttr: omitDomainRewriteAttr
      }, renderProps));
    }
  });
  addCommands(editor);
  editor.on('click', getPreventDefaultOnLinkClick(editor), true);
};